/**
 * Admin → Departments. The IRIS payroll name → iplicit department + class mapping.
 * An unmapped name blocks a submission rather than being guessed.
 */
import { useCallback, useEffect, useState } from "react";
import { getCostCentres, getDepartments, saveDepartment, type CostCentreRow, type DeptClass, type DeptMapping } from "../../payroll/api";

const CLASSES: DeptClass[] = ["Direct", "Semi Direct", "Indirect"];

const BLANK: DeptMapping = { payrollName: "", iplicitDept: "", class: "Direct", costCentre: "C9999", active: true };

export default function DepartmentsTab() {
  const [rows, setRows] = useState<DeptMapping[] | null>(null);
  const [centres, setCentres] = useState<CostCentreRow[]>([]);
  const [draft, setDraft] = useState<Record<string, DeptMapping>>({});
  const [adding, setAdding] = useState<DeptMapping | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [d, c] = await Promise.all([getDepartments(), getCostCentres()]);
      setRows(d); setCentres(c); setDraft({});
    } catch (e) { setError(msg(e)); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const edit = (r: DeptMapping, patch: Partial<DeptMapping>) =>
    setDraft((d) => ({ ...d, [r.payrollName]: { ...r, ...d[r.payrollName], ...patch } }));

  async function save(row: DeptMapping, isNew = false) {
    setError(null);
    if (!row.payrollName.trim() || !row.iplicitDept.trim()) { setError("Payroll name and iplicit department are both required."); return; }
    try {
      await saveDepartment(row);
      setNotice(`${row.payrollName} mapped to ${row.iplicitDept} (${row.class}).`);
      if (isNew) setAdding(null);
      await load();
    } catch (e) { setError(msg(e)); }
  }

  return (
    <section className="panel">
      <header className="panel__head">
        <div>
          <h2>Departments</h2>
          <p className="panel__sub">
            The payroll name must match the IRIS printout <strong>exactly</strong>. Semi Direct is for
            the “Indirect” splits inside operational areas (C&amp;W Indirect, Catering Indirect,
            MPD-Indirect, Operations-Indirect). Customer Services is Indirect.
          </p>
        </div>
        <button className="btn" onClick={() => setAdding({ ...BLANK })}>Add department</button>
      </header>

      {error && <div className="alert alert--error">{error}</div>}
      {notice && !error && <div className="alert alert--ok">{notice}</div>}

      {rows === null ? <p className="muted">Loading…</p> : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr><th>Payroll name (IRIS)</th><th>iplicit dept</th><th>Class</th><th>Cost centre</th><th>Active</th><th /></tr>
            </thead>
            <tbody>
              {adding && (
                <tr>
                  <td><input className="input" autoFocus value={adding.payrollName} onChange={(e) => setAdding({ ...adding, payrollName: e.target.value })} /></td>
                  <td><input className="input mono" value={adding.iplicitDept} onChange={(e) => setAdding({ ...adding, iplicitDept: e.target.value })} /></td>
                  <td>
                    <select className="select" value={adding.class} onChange={(e) => setAdding({ ...adding, class: e.target.value as DeptClass })}>
                      {CLASSES.map((c) => <option key={c}>{c}</option>)}
                    </select>
                  </td>
                  <td>
                    <select className="select" value={adding.costCentre} onChange={(e) => setAdding({ ...adding, costCentre: e.target.value })}>
                      {centres.map((c) => <option key={c.code} value={c.code}>{c.code}</option>)}
                    </select>
                  </td>
                  <td><input type="checkbox" checked={adding.active} onChange={(e) => setAdding({ ...adding, active: e.target.checked })} /></td>
                  <td className="row-actions">
                    <button className="btn btn--small btn--primary" onClick={() => void save(adding, true)}>Add</button>
                    <button className="btn btn--small" onClick={() => setAdding(null)}>Cancel</button>
                  </td>
                </tr>
              )}

              {rows.map((r) => {
                const d = draft[r.payrollName] ?? r;
                const dirty = !!draft[r.payrollName];
                return (
                  <tr key={r.payrollName}>
                    <td className="strong">{r.payrollName}</td>
                    <td><input className="input mono" value={d.iplicitDept} onChange={(e) => edit(r, { iplicitDept: e.target.value })} /></td>
                    <td>
                      <select className="select" value={d.class} onChange={(e) => edit(r, { class: e.target.value as DeptClass })}>
                        {CLASSES.map((c) => <option key={c}>{c}</option>)}
                      </select>
                    </td>
                    <td>
                      <select className="select" value={d.costCentre} onChange={(e) => edit(r, { costCentre: e.target.value })}>
                        {centres.map((c) => <option key={c.code} value={c.code}>{c.code}</option>)}
                      </select>
                    </td>
                    <td><input type="checkbox" checked={d.active} onChange={(e) => edit(r, { active: e.target.checked })} /></td>
                    <td><button className="btn btn--small" disabled={!dirty} onClick={() => void save(d)}>Save</button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <p className="muted">
        A payroll name that appears in the PDF but is missing here is reported on upload and the
        submission is refused until you map it.
      </p>
    </section>
  );
}

const msg = (e: unknown) => (e instanceof Error ? e.message : String(e));
